import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { BASE_PATH } from "../../routes";
import { doSearch, ISearchResult } from "../../helpers/search";
import { useDebounce } from "../../helpers/useDebounce";
import { isLandingPath } from "../../helpers/routes";
import { Autocomplete, SearchSuggestionOption } from "./Autocomplete";
import { Box, Heading, Hidden, Text } from "@origin-digital/ods-core";
import { FullPageCoverContainer, SearchContainer } from "./styles";
import { useDefaultSuggestions } from "./hooks/useDefaultSuggestions";
import { SearchEmptyState } from "./SearchEmptyState";
import { SearchEmptyStateSkeleton } from "./SearchEmptyStateSkeleton";
import { SearchingSkeleton } from "./SearchingSkeleton";

interface ISearchProps {
  heading?: string;
  subHeading?: string;
  searchPlaceholder?: string;
}

function mapResultsToSuggestionOptions(
  results: ISearchResult[]
): SearchSuggestionOption[] {
  return results
    .flatMap((result) =>
      result.title
        ? [
            {
              value: result.title,
              title: result.title,
              url: result.url,
              summary: result.summary,
              tabCategory: result.tabCategory,
              articleCategory: result.articleCategory,
              articleCategoryUrl: result.articleCategoryUrl,
            },
          ]
        : []
    )
    .filter(
      (suggestion, index, allSuggestions) =>
        allSuggestions.findIndex((item) => item.value === suggestion.value) ===
        index
    );
}

type SuggestionsState = {
  suggestions: SearchSuggestionOption[];
  isLoading: boolean;
  // Query string of the most-recent SUCCESSFUL response. Used by the dropdown
  // to tell "0 results for this exact input" apart from "still loading" or
  // "stale results from a previous query".
  resolvedQuery: string;
};

const emptySuggestionsState: SuggestionsState = {
  suggestions: [],
  isLoading: false,
  resolvedQuery: "",
};

function useSuggestions(searchQuery: string): SuggestionsState {
  const [state, setState] = React.useState<SuggestionsState>(
    emptySuggestionsState
  );

  React.useEffect(() => {
    let isActive = true;

    (async () => {
      if (!searchQuery) {
        if (isActive) {
          setState(emptySuggestionsState);
        }
        return;
      }

      setState((prev) => ({ ...prev, isLoading: true }));
      try {
        const response = await doSearch(searchQuery, {
          source: "autocomplete",
        });
        if (isActive) {
          setState({
            suggestions: mapResultsToSuggestionOptions(response.results),
            isLoading: false,
            resolvedQuery: searchQuery,
          });
        }
      } catch (error) {
        console.error("Search autocomplete failed:", { error, searchQuery });
        if (isActive) {
          setState((prev) => ({ ...prev, isLoading: false }));
        }
      }
    })();

    return () => {
      isActive = false;
    };
  }, [searchQuery]);

  return state;
}

type SearchHeadingProps = {
  heading?: string;
  subHeading?: string;
};

const SearchHeading: React.FC<SearchHeadingProps> = ({
  heading,
  subHeading,
}) => (
  <>
    {heading && (
      <Box width="full">
        <Hidden above="sm">
          <Heading component="h1" variant="h2" align="center">
            {heading}
          </Heading>
        </Hidden>

        <Hidden below="md">
          <Heading component="h1" variant="h1" align="center">
            {heading}
          </Heading>
        </Hidden>
      </Box>
    )}
    {subHeading && (
      <Box width="full" marginTop="small">
        <Text align="center" variant="subtitle" wordBreak={true}>
          {subHeading}
        </Text>
      </Box>
    )}
  </>
);

export const Search: React.FC<ISearchProps> = ({
  heading,
  subHeading,
  searchPlaceholder,
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isLandingPage = isLandingPath(pathname);

  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("term") || "");
  const [isFocused, setIsFocused] = useState(false);

  const handleFaqSelect = (question: string) => {
    setQuery(question);
    navigate(`${BASE_PATH}/search?term=${encodeURIComponent(question)}`);
  };

  const showEmptyState = isFocused && !query;
  const { faqs, quickActions, popularArticles, isLoading, isError } =
    useDefaultSuggestions(showEmptyState);

  const emptyStateContent =
    showEmptyState && !isError ? (
      isLoading ? (
        <SearchEmptyStateSkeleton />
      ) : (
        <SearchEmptyState
          faqs={faqs}
          quickActions={quickActions}
          popularArticles={popularArticles}
          onFaqSelect={handleFaqSelect}
        />
      )
    ) : null;

  const searchQuery = useDebounce(query, 200);
  const {
    suggestions,
    isLoading: isSuggestionsLoading,
    resolvedQuery,
  } = useSuggestions(searchQuery);

  const searchingContent = useMemo(
    () => (isSuggestionsLoading ? <SearchingSkeleton /> : undefined),
    [isSuggestionsLoading]
  );

  useEffect(() => {
    const searchTerm = searchParams.get("term");
    setQuery((currentQuery) => {
      const normalizedSearchTerm = searchTerm || "";
      return normalizedSearchTerm !== currentQuery
        ? normalizedSearchTerm
        : currentQuery;
    });
  }, [searchParams]);

  return (
    <SearchContainer>
      {isLandingPage && (
        <SearchHeading heading={heading} subHeading={subHeading} />
      )}
      <Box
        width="full"
        marginTop={isLandingPage ? { xs: "xlarge", lg: "xxlarge" } : {}}
      >
        <Autocomplete
          onFocusChange={setIsFocused}
          inputValue={query}
          setInputValue={setQuery}
          suggestions={suggestions}
          isLoading={isSuggestionsLoading}
          resolvedQuery={resolvedQuery}
          placeholder={searchPlaceholder}
          emptyState={emptyStateContent}
          searchingContent={searchingContent}
        />
      </Box>
      <FullPageCoverContainer show={isFocused} />
    </SearchContainer>
  );
};
